import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import SendIcon from '@material-ui/icons/Send'
import DescriptionIcon from '@material-ui/icons/Description'

export interface SendVendorOption {
    id: number
    name: string
    email?: string | null
}

interface SendSampleToVendorsDialogProps {
    open: boolean
    title: string
    documentName: string
    vendors: SendVendorOption[]
    vendorsLoading: boolean
    isSending: boolean
    emptyMessage?: string
    onClose: () => void
    onSend: (vendorIds: number[]) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: 12,
        height: 680,
        maxHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2.5, 3, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    headerIcon: {
        height: 40,
        width: 40,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    headerText: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
    },
    documentName: {
        marginTop: 4,
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    content: {
        padding: theme.spacing(2, 3, 1),
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    helper: {
        marginBottom: theme.spacing(2),
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
    },
    toolbarActions: {
        display: 'flex',
        gap: theme.spacing(0.5),
    },
    list: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingRight: 2,
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        background: theme.palette.background.paper,
        padding: theme.spacing(1.25, 1.5),
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.action.hover,
        },
    },
    cardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + '33',
    },
    cardDisabled: {
        cursor: 'default',
        opacity: 0.72,
        '&:hover': {
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.paper,
        },
    },
    vendorName: {
        fontWeight: 600,
        lineHeight: 1.3,
    },
    vendorMeta: {
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: theme.spacing(1),
        flex: 1,
        minHeight: 0,
        padding: theme.spacing(3),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 10,
        color: theme.palette.text.secondary,
    },
    actions: {
        padding: theme.spacing(1.5, 3, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
    },
    selectedLabel: {
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
}))

export const SendSampleToVendorsDialog: React.FC<SendSampleToVendorsDialogProps> = ({
    open,
    title,
    documentName,
    vendors,
    vendorsLoading,
    isSending,
    emptyMessage = 'No vendors found',
    onClose,
    onSend,
}) => {
    const classes = useStyles()
    const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([])
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        if (open) {
            setSelectedVendorIds([])
            setSearchQuery('')
        }
    }, [open])

    const filteredVendors = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        const sorted = [...vendors].sort((a, b) => a.name.localeCompare(b.name))
        if (!query) return sorted
        return sorted.filter(
            (vendor) =>
                vendor.name.toLowerCase().includes(query) ||
                vendor.email?.toLowerCase().includes(query),
        )
    }, [vendors, searchQuery])

    const selectableIds = filteredVendors
        .filter((vendor) => vendor.email)
        .map((vendor) => vendor.id)
    const allVisibleSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => selectedVendorIds.includes(id))

    const toggleVendor = (vendorId: number) => {
        setSelectedVendorIds((prev) =>
            prev.includes(vendorId)
                ? prev.filter((id) => id !== vendorId)
                : [...prev, vendorId],
        )
    }

    const toggleAllVisible = () => {
        setSelectedVendorIds((prev) => {
            if (allVisibleSelected) {
                return prev.filter((id) => !selectableIds.includes(id))
            }
            return Array.from(new Set([...prev, ...selectableIds]))
        })
    }

    return (
        <Dialog
            open={open}
            onClose={isSending ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.paper }}
        >
            <Box className={classes.header}>
                <Box className={classes.headerIcon}>
                    <SendIcon fontSize="small" />
                </Box>
                <Box className={classes.headerText}>
                    <Typography variant="h6" className={classes.title}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        className={classes.documentName}
                    >
                        {documentName}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={isSending}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                className={classes.content}
                style={{ overflow: 'hidden' }}
            >
                {vendorsLoading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={220}
                    >
                        <CircularProgress />
                    </Box>
                ) : vendors.length === 0 ? (
                    <Box className={classes.empty}>
                        <DescriptionIcon color="disabled" />
                        <Typography variant="body2">{emptyMessage}</Typography>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body2" className={classes.helper}>
                            Choose who should receive this sample. Each person
                            with an email gets a link to view it and a link to
                            upload their own. The link lasts 30 days.
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Search by name or email"
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            color="action"
                                            fontSize="small"
                                        />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box className={classes.toolbar}>
                            <Typography variant="caption" color="textSecondary">
                                {filteredVendors.length} vendor
                                {filteredVendors.length === 1 ? '' : 's'}
                            </Typography>
                            <Box className={classes.toolbarActions}>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={toggleAllVisible}
                                    disabled={selectableIds.length === 0}
                                >
                                    {allVisibleSelected
                                        ? 'Clear visible'
                                        : 'Select all'}
                                </Button>
                            </Box>
                        </Box>
                        <Box className={classes.list}>
                            {filteredVendors.length === 0 ? (
                                <Box className={classes.empty}>
                                    <Typography variant="body2">
                                        No vendors match that search.
                                    </Typography>
                                </Box>
                            ) : (
                                filteredVendors.map((vendor) => {
                                    const selected = selectedVendorIds.includes(
                                        vendor.id,
                                    )
                                    const canSend = Boolean(vendor.email)
                                    return (
                                        <Box
                                            key={vendor.id}
                                            className={`${classes.card} ${
                                                selected
                                                    ? classes.cardSelected
                                                    : ''
                                            } ${
                                                canSend
                                                    ? ''
                                                    : classes.cardDisabled
                                            }`}
                                            onClick={() => {
                                                if (canSend) {
                                                    toggleVendor(vendor.id)
                                                }
                                            }}
                                            role="checkbox"
                                            aria-checked={selected}
                                            aria-disabled={!canSend}
                                        >
                                            <Checkbox
                                                checked={selected}
                                                color="primary"
                                                size="small"
                                                disabled={!canSend}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                            <Box minWidth={0} flex={1}>
                                                <Typography
                                                    variant="body2"
                                                    className={
                                                        classes.vendorName
                                                    }
                                                >
                                                    {vendor.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    className={
                                                        classes.vendorMeta
                                                    }
                                                >
                                                    {vendor.email ||
                                                        'No email on file'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )
                                })
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Typography variant="body2" className={classes.selectedLabel}>
                    {selectedVendorIds.length === 0
                        ? 'None selected'
                        : `${selectedVendorIds.length} selected`}
                </Typography>
                <Box>
                    <Button onClick={onClose} disabled={isSending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onSend(selectedVendorIds)}
                        variant="contained"
                        color="primary"
                        disabled={isSending || selectedVendorIds.length === 0}
                        startIcon={
                            isSending ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SendIcon />
                            )
                        }
                    >
                        {isSending
                            ? 'Sending...'
                            : selectedVendorIds.length > 0
                            ? `Send to ${selectedVendorIds.length}`
                            : 'Send'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
